import {
	getCurrentScope,
	onScopeDispose,
	readonly,
	type Ref,
	ref,
} from 'vue';
import {
	callSafe,
	debounce,
} from '../utils/functions';

interface UseViewportReturn {
	fullViewport: Ref<Viewport>;
	visibleViewport: Ref<Viewport>;
	stop: () => void;
}

export type GetViewportWatchCallback = (viewport: Viewport) => void;

export interface Viewport {
	height: number;
	width: number;
}

export type ViewportType = 'full' | 'visible';

let preventVirtualKeyboardScroll = false;

let visibleViewport: Viewport = {
	height: Math.floor(window.visualViewport?.height || 0),
	width: Math.floor(window.visualViewport?.width || 0),
};

let fullViewport: Viewport = {
	width: Math.floor(
		Math.max(
			document.documentElement.clientWidth,
			window.innerWidth || 0,
		),
	),
	height: Math.floor(
		Math.max(
			document.documentElement.clientHeight,
			window.innerHeight || 0,
		),
	),
};
const watchers: Record<ViewportType, GetViewportWatchCallback[]> = {
	full: [],
	visible: [],
};
const onViewportChangeDebounce = debounce(

	onViewportChange,
	100,
);

const resizeObserver = new ResizeObserver(() => onViewportChange());
resizeObserver.observe(document.documentElement);

window.visualViewport?.addEventListener(
	'resize',

	() => onViewportChangeDebounce(),
);
window.visualViewport?.addEventListener(
	'scroll',

	() => onViewportChangeDebounce(),
);

function onViewportChange(): void {
	const currentFullViewport: Viewport = {
		width: Math.floor(
			Math.max(
				document.documentElement.clientWidth,
				window.innerWidth || 0,
			),
		),
		height: Math.floor(
			Math.max(
				document.documentElement.clientHeight,
				window.innerHeight || 0,
			),
		),
	};
	const currentVisibleViewport: Viewport = {
		height: Math.floor(window.visualViewport?.height || 0),
		width: Math.floor(window.visualViewport?.width || 0),
	};

	if (
		currentFullViewport.height !== fullViewport.height
		|| currentFullViewport.width !== fullViewport.width
	) {
		fullViewport = currentFullViewport;

		for (const watcher of watchers.full) {
			callSafe(() => watcher(currentFullViewport));
		}
	}

	if (
		currentVisibleViewport.height !== visibleViewport.height
		|| currentVisibleViewport.width !== visibleViewport.width
	) {
		visibleViewport = currentVisibleViewport;

		for (const watcher of watchers.visible) {
			callSafe(() => watcher(currentVisibleViewport));
		}
	}
}

function onWebAppScroll(event: TouchEvent): void {
	if (
		visibleViewport.height !== window.innerHeight
		|| visibleViewport.width !== window.innerWidth
	) {
		event.preventDefault();
	}
}

function useViewport(): UseViewportReturn {
	const fullViewportState = ref(fullViewport);
	const visibleViewportState = ref(visibleViewport);
	const disposers: Array<() => void> = [
		watch((value) => {
			fullViewportState.value = value;
		}),
		watch(
			(value) => {
				visibleViewportState.value = value;
			},
			'visible',
		),
	];

	const stop = () => {
		while (disposers.length) {
			const disposer = disposers.pop();
			disposer?.();
		}
	};

	if (getCurrentScope()) {
		onScopeDispose(stop);
	}

	return {
		fullViewport: readonly(fullViewportState),
		visibleViewport: readonly(visibleViewportState),
		stop,
	};
}

export function configureAppHeight(): void {
	if (
		document.documentElement.style.getPropertyValue('--app-height')
		&& document.documentElement.style.getPropertyValue('--app-full-height')
	) {
		return;
	}

	if (visibleViewport.height) {
		document.documentElement.style.setProperty(
			'--app-height',
			`${visibleViewport.height}px`,
		);
	} else {
		document.documentElement.style.removeProperty('--app-height');
	}

	if (fullViewport.height) {
		document.documentElement.style.setProperty(
			'--app-full-height',
			`${fullViewport.height}px`,
		);
	} else {
		document.documentElement.style.removeProperty('--app-full-height');
	}

	watch(
		() => {
			if (visibleViewport.height) {
				document.documentElement.style.setProperty(
					'--app-height',
					`${visibleViewport.height}px`,
				);
			} else {
				document.documentElement.style.removeProperty('--app-height');
			}

			if (fullViewport.height) {
				document.documentElement.style.setProperty(
					'--app-full-height',
					`${fullViewport.height}px`,
				);
			} else {
				document.documentElement.style.removeProperty('--app-full-height');
			}
		},
		[
			'full',
			'visible',
		],
	);
}

export function preventScrollOnVirtualKeyboard(prevent = true): void {
	if (preventVirtualKeyboardScroll === prevent) {
		return;
	}

	preventVirtualKeyboardScroll = prevent;
	const webAppElement = document.getElementById('webapp');

	if (preventVirtualKeyboardScroll) {
		webAppElement?.addEventListener(
			'touchmove',
			onWebAppScroll,
		);
	} else {
		webAppElement?.removeEventListener(
			'touchmove',
			onWebAppScroll,
		);
	}
}

export function watch(
	callback: GetViewportWatchCallback,
	type: ViewportType[] | ViewportType = 'full',
): () => void {
	const arrayType = (
		Array.isArray(type)
			? type
			: [type]
	);

	for (const singleType of arrayType) {
		watchers[singleType].push(callback);
	}

	return () => {
		for (const singleType of arrayType) {
			const index = watchers[singleType].indexOf(callback);

			if (index > -1) {
				watchers[singleType].splice(
					index,
					1,
				);
			}
		}
	};
}

export {
	fullViewport,
	visibleViewport,
	useViewport,
};
